// Acesso a dados da caixa de entrada.
//
// Duas regras herdadas da entrega do backend (Fases 0–2), e as duas já foram
// motivo de bug em outro módulo:
//
//  1. Conexão sai da VIEW `inbox_connections_public`. A tabela guarda
//     `credentials_ref` e o grant é por coluna — query direta devolve
//     "permission denied".
//  2. Mensagem se ordena por `provider_timestamp`. `created_at` é quando NÓS
//     gravamos; numa fila atrasada os dois divergem e a thread sai fora de ordem.

import { supabase } from '../../../src/lib/supabase';
import type { ConexaoPublica, Contato, ConversaLista, Mensagem } from './tipos';

/** Quantas mensagens a thread busca por vez. */
export const PAGINA_THREAD = 50;

/**
 * A org do usuário no Inbox.
 *
 * O acesso ao módulo vem de `inbox_members`, NÃO de `profiles.role`: quem não
 * está na tabela não é membro, e a RLS já devolve tudo vazio para essa pessoa.
 * Devolver null aqui é o que a tela usa para mostrar "sem acesso" em vez de uma
 * caixa vazia, que pareceria um bug.
 */
export async function carregarOrgId(userId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('inbox_members')
    .select('org_id')
    .eq('user_id', userId)
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data?.org_id ?? null;
}

export async function carregarConexoes(orgId: string): Promise<ConexaoPublica[]> {
  const { data, error } = await supabase
    .from('inbox_connections_public')
    .select('id, org_id, channel_type, display_name, external_id, status, capabilities, metadata, last_error, last_seen_at')
    .eq('org_id', orgId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as ConexaoPublica[];
}

export interface FiltroConversas {
  orgId: string;
  /** null = todas. */
  status?: 'open' | 'pending' | 'resolved' | null;
  /** Só as minhas e as sem dono. */
  apenasMinhas?: boolean;
  meuId?: string;
  busca?: string;
  /** Tipos de canal a mostrar. null/vazio = todos. */
  canais?: string[] | null;
  limite?: number;
}

/**
 * A coluna da esquerda.
 *
 * Vem da view `inbox_conversas_lista`, que já traz contato, conexão e a última
 * mensagem por LATERAL. Sem ela seriam 1 + N consultas por render — uma por
 * conversa só para descobrir a linha de prévia.
 *
 * Não filtre por responsável achando que é segurança: a RLS já limita o que o
 * agente enxerga. O filtro aqui é conveniência de quem está atendendo.
 */
export async function carregarConversas(f: FiltroConversas): Promise<ConversaLista[]> {
  let q = supabase
    .from('inbox_conversas_lista')
    .select('*')
    .eq('org_id', f.orgId)
    .order('last_message_at', { ascending: false, nullsFirst: false })
    .limit(f.limite ?? 200);

  if (f.status) q = q.eq('status', f.status);
  // Sem este filtro, a caixa de e-mail ficava soterrada: a lista ordena por
  // atividade recente e o WhatsApp gera muito mais movimento. Em 23/09/2026
  // eram 3 conversas de e-mail embaixo de 52 de WhatsApp — quem procurava
  // concluía que o e-mail não estava chegando.
  if (f.canais?.length) q = q.in('channel_type', f.canais);
  if (f.apenasMinhas && f.meuId) q = q.or(`assignee_id.eq.${f.meuId},assignee_id.is.null`);

  const termo = (f.busca ?? '').trim();
  if (termo) {
    // Vírgula e parêntese são a sintaxe do filtro `or` do PostgREST: um termo
    // de busca com esses caracteres viraria filtro inválido, não busca vazia.
    // O protocolo entra aqui porque é assim que o cliente se identifica no
    // telefone: "estou ligando sobre o VG-2026-000123".
    const escapado = termo.replace(/[%,()]/g, ' ');
    q = q.or(`contato_nome.ilike.%${escapado}%,contato_telefone.ilike.%${escapado}%,ultima_mensagem.ilike.%${escapado}%,ticket_protocolo.ilike.%${escapado}%`);
  }

  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as ConversaLista[];
}

/**
 * Uma página da thread, da mais nova para a mais antiga, devolvida já na ordem
 * de leitura (antiga → nova).
 *
 * `anteriorA` é keyset, não offset: numa conversa que recebe mensagem enquanto
 * o operador rola para cima, o offset repetiria ou pularia linhas.
 */
export async function carregarMensagens(
  conversationId: string,
  anteriorA?: string | null,
): Promise<Mensagem[]> {
  let q = supabase
    .from('inbox_messages')
    .select('id, conversation_id, direction, type, body, subject, body_html, body_visible, recipients, provider_message_id, provider_timestamp, status, failure_reason, sender_user_id, reply_to_external_id, payload, anexos:inbox_attachments(id, message_id, storage_path, mime_type, file_name, size_bytes)')
    .eq('conversation_id', conversationId)
    .order('provider_timestamp', { ascending: false })
    .limit(PAGINA_THREAD);

  if (anteriorA) q = q.lt('provider_timestamp', anteriorA);

  const { data, error } = await q;
  if (error) throw error;
  return ((data ?? []) as unknown as Mensagem[]).slice().reverse();
}

export async function carregarContato(contactId: string): Promise<Contato | null> {
  const { data, error } = await supabase
    .from('inbox_contacts')
    .select('id, nome, telefone, email, avatar_url, unidade_consumidora_id, negociacao_id, created_at')
    .eq('id', contactId)
    .maybeSingle();
  if (error) throw error;
  return (data as Contato) ?? null;
}

export interface VinculoUC {
  id: number;
  nome_cliente: string | null;
  cidade: string | null;
}

/** O que amarra a conversa ao cadastro: a UC do cliente. */
export async function carregarUnidade(id: number): Promise<VinculoUC | null> {
  const { data, error } = await supabase
    .from('unidades_consumidoras')
    .select('id, nome_cliente, cidade')
    .eq('id', id)
    .maybeSingle();
  if (error) return null;
  return (data as VinculoUC) ?? null;
}

export interface VinculoNegociacao {
  id: number;
  nome_cliente: string | null;
  etapa: string | null;
}

export async function carregarNegociacao(id: number): Promise<VinculoNegociacao | null> {
  const { data, error } = await supabase
    .from('negociacoes')
    .select('id, nome_cliente, etapa')
    .eq('id', id)
    .maybeSingle();
  if (error) return null;
  return (data as VinculoNegociacao) ?? null;
}

/** Abrir a conversa zera o contador. Falha aqui não pode derrubar a leitura. */
export async function marcarComoLida(conversationId: string): Promise<void> {
  const { error } = await supabase
    .from('inbox_conversations')
    .update({ unread_count: 0 })
    .eq('id', conversationId)
    .gt('unread_count', 0);
  if (error) console.warn('[inbox] não consegui zerar as não lidas:', error.message);
}

/**
 * Anexo mora em bucket PRIVADO. Sem URL assinada a imagem não carrega, e o
 * `<img>` falha em silêncio — parece bug de layout, é permissão.
 */
export async function urlAssinada(storagePath: string, segundos = 3600): Promise<string | null> {
  const { data, error } = await supabase.storage.from('inbox-media').createSignedUrl(storagePath, segundos);
  if (error) return null;
  return data?.signedUrl ?? null;
}

/**
 * Envio.
 *
 * ⚠️ `functions.invoke` engole a causa: em resposta não-2xx o SDK devolve só
 * "Edge Function returned a non-2xx status code". O motivo que o atendente
 * precisa ler — "o canal está desconectado" — está em `error.context`. Mesmo
 * padrão de `src/lib/gatewayResolver.ts`.
 */
export async function enviarMensagem(
  conversationId: string,
  texto: string,
  /**
   * Fase 13 — resposta a comentário do Instagram.
   *
   * Não é uma variação do envio de texto: no Instagram, comentário e DM são
   * endpoints DIFERENTES (`/{comment_id}/replies` e `/{ig_id}/messages`).
   * Mandar como texto numa thread de comentário faria a resposta ir para o
   * direct do cliente em vez de aparecer embaixo do post — e ninguém notaria,
   * porque as duas coisas "funcionam".
   */
  comentarioAlvo?: string | null,
): Promise<Mensagem> {
  const payload = comentarioAlvo
    ? { type: 'comment_reply', body: texto, replyToExternalId: comentarioAlvo }
    : { type: 'text', body: texto };

  const { data, error } = await supabase.functions.invoke('inbox-send', {
    body: { conversation_id: conversationId, payload },
  });

  if (error) {
    const detalhe = await (error as any)?.context?.json?.().catch(() => null);
    throw new Error(detalhe?.error || error.message || 'Falha ao enviar a mensagem.');
  }
  if (!data?.message) throw new Error('O envio respondeu sem a mensagem.');
  return data.message as Mensagem;
}

/** O botão "tentar de novo" de uma mensagem que falhou. */
export async function reenviarMensagem(messageId: string): Promise<void> {
  const { error } = await supabase.rpc('inbox_reenviar_mensagem', { p_message_id: messageId });
  if (error) throw new Error(error.message);
}
